import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { map } from 'lodash';
import { GridCustomColDef } from '../../../types';
import { chipStyles, formatPercent, formatPrice } from '../../../../utils/helpers';
import { PaymentFrequency } from '../../../../utils/constants';
import {
  advancedEnumOperators,
  advancedNumericOperators,
  advancedOperators,
  advancedBooleanOperators
} from '../../../Compounds/UpstackDataGrid/helpers';
import { ComponentName } from '../../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { Text } from '../../../Atoms/Text';
import { handleAssetClick } from '../../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

const financialColumns = {
  chargeRecurringExpected: 'Expected MRC',
  chargeRecurringActual: 'Actual MRC',
  chargeRecurringDifference: 'MRC Diff',
  revenueRecurringExpected: 'Expected MRR',
  revenueRecurringActual: 'Actual MRR',
  revenueRecurringDifference: 'MRR Diff',
  revenueRecurringPercentExpected: 'Expected %',
  revenueRecurringPercentActual: 'Actual %',
  chargeOnetimeExpected: 'Expected NRC',
  chargeOnetimeActual: 'Actual NRC',
  chargeOnetimeDifference: 'NRC Diff',
  revenueOnetimeExpected: 'Expected NRR',
  revenueOnetimeActual: 'Actual NRR',
  revenueOnetimeDifference: 'NRR Diff',
  revenueRecurringNetActual: 'Net MRR',
  revenueOneTimeNetActual: 'Net NRR',
  revenueTrueupExpected: 'Expected True-Up',
  revenueTrueupActual: 'Actual True-Up',
  revenueSpiffActual: 'SPIFF'
};

export const DefaultAttributionGridColumnDefs = (): GridCustomColDef[] => [
  {
    field: 'directSellerName',
    headerName: 'Direct Seller',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 150,
    filterOperators: advancedOperators(),
    hideable: false,
    pinned: 'left'
  },
  {
    field: 'assetName',
    headerName: 'Asset',
    sortable: true,
    filterable: true,
    width: 300,
    filterOperators: advancedOperators(),
    renderCell: (params: GridCellParams) => {
      const type = params.row.assetSfId ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(params.row.assetId, type)}>
          <Text
            size="sm14"
            color="indigo">
            {params?.row.assetName as string}
          </Text>
        </button>
      );
    },
    hideable: false,
    pinned: 'left'
  },
  {
    field: 'directSalesPaidPercent',
    headerName: 'Direct Sales Paid %',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  ...map(
    Object.keys(financialColumns),
    (key) =>
      ({
        headerName: financialColumns[key as keyof typeof financialColumns],
        field: key,
        renderCell: (params: GridCellParams) => {
          const value = params.value as number | undefined;
          const isPercent = key.toLowerCase().includes('percent');
          if (value === undefined || value === null) return '';
          if (isPercent) {
            return (
              <span className={`float-right ${Number(value) < 0 ? 'text-price-red' : ''}`}>
                {formatPercent(value, true)}
              </span>
            );
          }
          return (
            <span className={`float-right ${value && Number(value) < 0 ? 'text-price-red' : ''}`}>
              {formatPrice((value as number) || 0)}
            </span>
          );
        },
        flex: 1,
        minWidth: 120,
        filterable: true,
        sortable: true,
        headerAlign: 'right',
        headerClassName: key.includes('NetActual') ? '!bg-azure-200' : undefined,
        cellClassName: key.includes('NetActual') ? '!bg-azure-100' : undefined,
        filterOperators: advancedNumericOperators()
      }) as GridCustomColDef
  ),
  {
    valueOptions: [...Object.values(PaymentFrequency ?? {}), 'Mix'],
    field: 'paymentFrequency',
    headerName: 'Payment Frequency',
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 100,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    },
    filterOperators: advancedEnumOperators(true)
  },
  {
    field: 'allocationCount',
    headerName: 'Links',
    align: 'right',
    sortable: true,
    filterable: true,
    flex: 0.5,
    minWidth: 60,
    filterOperators: advancedNumericOperators()
  },
  {
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    field: 'openTicket',
    headerName: 'Open Ticket',
    sortable: true,
    filterable: true,
    flex: 0.75,
    minWidth: 90,
    filterOperators: advancedBooleanOperators()
  },
  {
    renderCell: (params: GridCellParams) => (params.value as string[])?.join(', '),
    field: 'allocationMethods',
    headerName: 'Methods',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 150,
    filterOperators: advancedOperators()
  }
];
